import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PaSelect from "components/field/PaSelect";
import PaTextField from "components/field/PaTextField";
import DataInputDefinition from "../definition/DataInputDefinition";
import SignDefinition from "../definition/SignDefinition";
import PaymentDefinition from "../definition/PaymentDefinition";
import StripePaymentDefinition from "../definition/StripePaymentDefinition";
import GenerateDocumentDefinition from "../definition/GenerateDocumentDefinition";
import { StepCreateRequest } from "api/main/data-contracts";
import { MetaInformation } from "../Process";
import { stepTypeConfig } from "./StepNode";
import { adminTheme } from "theme/adminTheme";

// Step types that should not be available for selection
const HIDDEN_STEP_TYPES = ["PAYMENT", "NOTIFICATION", "DATA_OUTPUT"];

interface StepDetailsPanelProps {
  selectedStepIndex: number | null;
  step: StepCreateRequest | null;
  metaInfo?: MetaInformation;
  onClose: () => void;
  values: any; // Formik values
}

export default function StepDetailsPanel({
  selectedStepIndex,
  step,
  metaInfo,
  onClose,
  values,
}: StepDetailsPanelProps) {
  if (selectedStepIndex === null || !step) {
    return (
      <Box
        sx={{
          width: 420,
          height: "100%",
          bgcolor: adminTheme.colors.backgroundAlt,
          borderLeft: `1px solid ${adminTheme.colors.border}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: 320,
          }}
        >
          {/* Icon illustration */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: adminTheme.borderRadius.xl,
              background: `linear-gradient(135deg, ${adminTheme.colors.primary}15 0%, ${adminTheme.colors.primary}08 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <AccountTreeOutlinedIcon
              sx={{
                fontSize: 56,
                color: adminTheme.colors.primary,
                opacity: 0.6,
              }}
            />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: adminTheme.colors.textPrimary,
              mb: 1.5,
            }}
          >
            Žingsnis nepasirinktas
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: adminTheme.colors.textSecondary,
              lineHeight: 1.6,
            }}
          >
            Pasirinkite žingsnį iš darbinės srities, kad galėtumėte jį
            redaguoti ir konfigūruoti
          </Typography>
        </Box>
      </Box>
    );
  }

  const config = stepTypeConfig[step.type || ""] || {
    icon: null,
    color: "#757575",
    label: step.type || "Unknown",
  };

  return (
    <Box
      sx={{
        width: 420,
        height: "100%",
        bgcolor: adminTheme.colors.background,
        borderLeft: `1px solid ${adminTheme.colors.border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${config.color}15 0%, ${config.color}08 100%)`,
          borderBottom: `2px solid ${config.color}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: adminTheme.borderRadius.lg,
              bgcolor: config.color,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "1.25rem",
              boxShadow: `0 4px 12px ${config.color}40`,
            }}
          >
            {selectedStepIndex + 1}
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: adminTheme.colors.textMuted,
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Žingsnio konfigūracija
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: adminTheme.colors.textPrimary,
                mt: 0.5,
              }}
            >
              {config.label}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            bgcolor: `${config.color}20`,
            "&:hover": { bgcolor: `${config.color}30` },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Configuration Form */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Basic Information Section */}
        <Accordion
          defaultExpanded
          sx={{
            boxShadow: "none",
            border: `1px solid ${adminTheme.colors.border}`,
            borderRadius: `${adminTheme.borderRadius.md} !important`,
            "&:before": { display: "none" },
            "&.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: adminTheme.colors.backgroundAlt,
              borderRadius: adminTheme.borderRadius.md,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: adminTheme.borderRadius.sm,
                  bgcolor: `${adminTheme.colors.primary}15`,
                  color: adminTheme.colors.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Pagrindinė informacija
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              bgcolor: adminTheme.colors.background,
            }}
          >
            <PaTextField
              name={`steps.${selectedStepIndex}.name`}
              label="Žingsnio pavadinimas"
            />

            <PaTextField
              name={`steps.${selectedStepIndex}.description`}
              label="Aprašymas"
              rows={3}
            />

            <PaSelect
              name={`steps.${selectedStepIndex}.type`}
              label="Žingsnio tipas"
              tooltip="Pasirinkite, koks yra žingsnio tipas."
              options={
                metaInfo?.stepTypes
                  ?.filter((stepType) => !HIDDEN_STEP_TYPES.includes(stepType))
                  .map((stepType) => ({
                    value: stepType,
                    label: stepType,
                  })) ?? []
              }
            />

            <PaSelect
              name={`steps.${selectedStepIndex}.executors`}
              label="Atlikėjai"
              tooltip="Pasirinkite, kas atliks šį žingsnį."
              options={
                metaInfo?.stepExecutors?.map((executor) => ({
                  value: executor,
                  label: executor,
                })) ?? []
              }
              multiple
            />
          </AccordionDetails>
        </Accordion>

        {/* Notifications Section */}
        <Accordion
          defaultExpanded
          sx={{
            boxShadow: "none",
            border: `1px solid ${adminTheme.colors.border}`,
            borderRadius: `${adminTheme.borderRadius.md} !important`,
            "&:before": { display: "none" },
            "&.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: adminTheme.colors.backgroundAlt,
              borderRadius: adminTheme.borderRadius.md,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: adminTheme.borderRadius.sm,
                  bgcolor: `${adminTheme.colors.warning}15`,
                  color: adminTheme.colors.warning,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <NotificationsOutlinedIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Pranešimai
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              bgcolor: adminTheme.colors.background,
            }}
          >
            <PaSelect
              name={`steps.${selectedStepIndex}.notificationType`}
              label="Pranešimo tipas"
              tooltip="Pasirinkite, kada išsiųsti laišką."
              options={
                metaInfo?.notificationTypes?.map((type) => ({
                  value: type,
                  label: type,
                })) ?? []
              }
            />

            {values.steps &&
              values.steps[selectedStepIndex].notificationType !== "NONE" && (
                <PaSelect
                  name={`steps.${selectedStepIndex}.emailTemplateId`}
                  label="Laiško šablonas"
                  tooltip="Pasirinkite, kurį laiško šabloną naudoti."
                  options={
                    metaInfo?.emailTemplateTypes?.map((emailTemplate) => ({
                      value: emailTemplate?.id,
                      label: `${emailTemplate?.id}. ${emailTemplate?.subject}`,
                    })) ?? []
                  }
                />
              )}
          </AccordionDetails>
        </Accordion>

        {/* Step-specific configuration - only show for step types that have configuration */}
        {["DATA_INPUT", "SIGN", "PAYMENT", "STRIPE_PAYMENT", "GENERATE_DOCUMENT"].includes(step?.type || "") && (
          <Accordion
            defaultExpanded
            sx={{
              boxShadow: "none",
              border: `1px solid ${adminTheme.colors.border}`,
              borderRadius: `${adminTheme.borderRadius.md} !important`,
              "&:before": { display: "none" },
              "&.Mui-expanded": {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: adminTheme.colors.backgroundAlt,
                borderRadius: adminTheme.borderRadius.md,
                "&.Mui-expanded": {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: adminTheme.borderRadius.sm,
                    bgcolor: `${config.color}15`,
                    color: config.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SettingsOutlinedIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Žingsnio konfigūracija
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                bgcolor: adminTheme.colors.background,
              }}
            >
              {step?.type === "DATA_INPUT" && (
                <DataInputDefinition
                  meta={metaInfo}
                  step={step}
                  stepIndex={selectedStepIndex}
                />
              )}

              {step?.type === "SIGN" && (
                <SignDefinition
                  meta={metaInfo}
                  stepIndex={selectedStepIndex}
                  values={values}
                />
              )}

              {step?.type === "PAYMENT" && (
                <PaymentDefinition stepIndex={selectedStepIndex} meta={metaInfo} />
              )}

              {step?.type === "STRIPE_PAYMENT" && (
                <StripePaymentDefinition stepIndex={selectedStepIndex} />
              )}

              {step?.type === "GENERATE_DOCUMENT" && (
                <GenerateDocumentDefinition stepIndex={selectedStepIndex} meta={metaInfo} />
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
}
